import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { authClient } from '@/lib/auth-client';
import { api } from '@/lib/api';
import type { MeDto, UserDto } from '@scala-watch/shared';

interface MeResponse extends Omit<MeDto, never> {
  activeSettings: Record<string, unknown> | null;
}

export const useAuthStore = defineStore('auth', () => {
  const user = ref<UserDto | null>(null);
  const memberships = ref<MeDto['memberships']>([]);
  const activeAccount = ref<MeDto['activeAccount']>(null);
  const activeSettings = ref<Record<string, unknown> | null>(null);
  const ready = ref(false);
  const loading = ref(false);

  const isAuthenticated = computed(() => user.value !== null);
  const isSuperAdmin = computed(() => user.value?.isSuperAdmin === true);

  async function refresh(): Promise<void> {
    loading.value = true;
    try {
      const me = await api<MeResponse>('/api/me');
      user.value = me.user;
      memberships.value = me.memberships;
      activeAccount.value = me.activeAccount;
      activeSettings.value = me.activeSettings;
    } catch {
      user.value = null;
      memberships.value = [];
      activeAccount.value = null;
      activeSettings.value = null;
    } finally {
      loading.value = false;
      ready.value = true;
    }
  }

  async function signIn(email: string, password: string): Promise<{ twoFactorRequired: boolean }> {
    const res = await authClient.signIn.email({ email, password });
    if (res.error) throw new Error(res.error.message ?? 'Sign-in failed');
    const data = res.data as { twoFactorRedirect?: boolean } | null;
    const twoFactorRequired = Boolean(data?.twoFactorRedirect);
    if (!twoFactorRequired) await refresh();
    return { twoFactorRequired };
  }

  async function verifyTwoFactor(code: string): Promise<void> {
    const res = await authClient.twoFactor.verifyTotp({ code });
    if (res.error) throw new Error(res.error.message ?? '2FA verification failed');
    await refresh();
  }

  async function signOut(): Promise<void> {
    await api('/api/me/logout', { method: 'POST' });
    user.value = null;
    memberships.value = [];
    activeAccount.value = null;
    activeSettings.value = null;
  }

  async function requestPasswordReset(email: string): Promise<void> {
    const res = await authClient.requestPasswordReset({
      email,
      redirectTo: `${window.location.origin}/reset-password`,
    });
    if (res.error) throw new Error(res.error.message ?? 'Failed to send reset email');
  }

  async function resetPassword(token: string, password: string): Promise<void> {
    const res = await authClient.resetPassword({ newPassword: password, token });
    if (res.error) throw new Error(res.error.message ?? 'Failed to reset password');
  }

  /**
   * Used by the invite-link "set password" flow. Better Auth's
   * `resetPassword` works for both reset and first-time set since the only
   * difference is which email triggered the token.
   */
  async function setPassword(token: string, password: string): Promise<void> {
    return resetPassword(token, password);
  }

  return {
    user,
    memberships,
    activeAccount,
    activeSettings,
    ready,
    loading,
    isAuthenticated,
    isSuperAdmin,
    refresh,
    signIn,
    verifyTwoFactor,
    signOut,
    requestPasswordReset,
    resetPassword,
    setPassword,
  };
});
